import React, { MouseEvent, useState } from 'react';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { LocalesInfo } from 'client/i18n/types';
import i18nLocales from 'client/i18n/locales.json';
import { GlobeIcon } from '../svgs/GlobeIcon';
import {
  StyledLanguageSelectorContainer,
  StyledLanguageSelectorDisplay,
  StyledLanguageSelectorList,
  StyledLanguageSelectorListContainer,
  StyledLanguageSelectorListItem,
} from './styles';

type I18NLanguageProp = {
  label: string;
  value: string;
};

const pseudoOption = {
  label: 'Pseudo',
  value: 'pseudo',
} as I18NLanguageProp;

// TODO: https://anchorfm.atlassian.net/browse/CAST-2659 - Remove filtered languages with Q1 Launch
const q1LaunchLanguages = [
  'hi',
  'hu',
  'id',
  'ja',
  'ko',
  'ms',
  'pt-BR',
  'ru',
  'th',
  'tr',
  'vi',
];

const localesOptions = Object.entries(i18nLocales as LocalesInfo).reduce<
  I18NLanguageProp[]
>(
  (acc, [locale, { displayName }]) =>
    acc.concat([{ label: displayName, value: locale }]),
  []
);

export function I18NLanguageSelector({ isDesktop }: { isDesktop?: boolean }) {
  const { changeLocale, enableI18N, locale, showPseudoLang } = useLocale();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleClick = (event: MouseEvent<HTMLLIElement>) => {
    const newLocale =
      (event.target as HTMLInputElement).getAttribute('value') || '';
    // TODO https://anchorfm.atlassian.net/browse/WHEEL-807: Find Solution Without Utilizing GA Global Object
    if (window.ga !== undefined) {
      const gaEventParams = {
        eventCategory: 'Profile Page Subscribe',
        eventAction: 'Language Selector',
        eventLabel: newLocale,
      };

      // TODO https://anchorfm.atlassian.net/browse/WHEEL-807: Find Solution Without Utilizing GA Global Object
      window.ga('send', 'event', gaEventParams);
    }

    changeLocale(newLocale);
  };

  // TODO: https://anchorfm.atlassian.net/browse/CAST-2659 - Remove filtered languages with Q1 Launch
  const currentLocaleOptions = enableI18N
    ? [...localesOptions, ...(showPseudoLang ? [pseudoOption] : [])]
    : localesOptions.filter(
        (localeOpt: I18NLanguageProp) =>
          !q1LaunchLanguages.includes(localeOpt.value)
      );

  return (
    <StyledLanguageSelectorContainer
      data-cy="language-selector__nav"
      onBlur={() => (isOpen ? setIsOpen(false) : null)}
      onClick={() => setIsOpen(!isOpen)}
      tabIndex={0}
    >
      <StyledLanguageSelectorDisplay
        className={isOpen ? 'opened' : 'closed'}
        data-cy="language-selector__display__nav"
      >
        <GlobeIcon
          additionalAriaId={`languageSelectorNav${isDesktop ? 'Desktop' : ''}`}
        />
        <span>{locale}</span>
        <div className="selector-caret" />
      </StyledLanguageSelectorDisplay>
      {isOpen && currentLocaleOptions.length && (
        <StyledLanguageSelectorListContainer isDesktop={isDesktop}>
          <StyledLanguageSelectorList isDesktop={isDesktop}>
            {currentLocaleOptions.map((option: I18NLanguageProp) => (
              <StyledLanguageSelectorListItem
                isSelected={locale === option.value}
                key={option.label}
                onClick={handleClick}
                value={option.value}
              >
                {option.label}
              </StyledLanguageSelectorListItem>
            ))}
          </StyledLanguageSelectorList>
        </StyledLanguageSelectorListContainer>
      )}
    </StyledLanguageSelectorContainer>
  );
}
